import { Box, Center, Flex, Link, Spinner, Text } from "@chakra-ui/react";
import { SignIn, SignUp, useAuth } from "@clerk/clerk-react";
import { useMemo } from "react";
import { Helmet } from "react-helmet-async";

import CenteredLayout from "@/common/components/CenteredLayout";
import { useEnvironmentUnsafe } from "@/common/contexts/EnvironmentContext";
import { LoginUrl, SignupUrl } from "@reflag/shared/urls";

import GoToHome from "../components/GoToHome";

type Props = {
  mode: "login" | "signup";
};

export default function Auth({ mode }: Props) {
  const { isLoaded } = useAuth();
  const envState = useEnvironmentUnsafe();

  const redirectUrl =
    new URLSearchParams(window.location.search).get("redirect_url") ?? "/";

  const redirectParams = new URLSearchParams({
    redirect_url: redirectUrl,
  });
  const isInvite = redirectUrl.includes("/invite/");

  const text = useMemo<string | null>(() => {
    if (isInvite) {
      if (mode === "signup") {
        return "To accept your invitation, sign up below.";
      } else {
        return "To accept your invitation, sign in below.";
      }
    }
    return null;
  }, [isInvite, mode]);

  if (envState?.user) {
    return <GoToHome />;
  }

  return (
    <CenteredLayout bg="appSidebarBackground">
      <Helmet>
        <title>{mode === "login" ? "Login" : "Sign-up"}</title>
      </Helmet>
      <Box>
        {isLoaded ? (
          <Flex alignItems="center" direction="column" gap={8} maxW="sm">
            {text && (
              <Text
                color="dimmed"
                fontSize="sm"
                fontWeight="medium"
                textAlign="center"
              >
                {text}
              </Text>
            )}
            {mode === "login" ? (
              <SignIn
                fallbackRedirectUrl={redirectUrl}
                signUpFallbackRedirectUrl={redirectUrl}
                signUpUrl={SignupUrl(redirectParams)}
              />
            ) : (
              <SignUp
                fallbackRedirectUrl={redirectUrl}
                signInFallbackRedirectUrl={redirectUrl}
                signInUrl={LoginUrl(redirectParams)}
              />
            )}
            <Text
              color="dimmed"
              fontSize="xs"
              fontWeight="medium"
              textAlign="center"
            >
              By continuing, you&apos;re agreeing to our{" "}
              <Link href="https://reflag.com/pages/policies" target="_blank">
                policies
              </Link>
            </Text>
          </Flex>
        ) : (
          <Center h="100vh">
            <Spinner />
          </Center>
        )}
      </Box>
    </CenteredLayout>
  );
}
